import { useEffect, useRef } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";

const TOOLBAR = [
  [{ header: [3, 4, false] }],
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link"],
  ["clean"],
];

// Quill is instantiated inside useEffect on a plain ref element (post-commit),
// so it never participates in React 19's concurrent render phase. This avoids
// the recoverable "concurrent rendering" error that react-quill triggers.
export default function RichText({ value, onChange, label }) {
  const containerRef = useRef(null);
  const quillRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const container = containerRef.current;
    const editorEl = document.createElement("div");
    container.appendChild(editorEl);
    const quill = new Quill(editorEl, {
      theme: "snow",
      modules: { toolbar: TOOLBAR },
    });
    quillRef.current = quill;

    if (value) quill.clipboard.dangerouslyPasteHTML(value);

    quill.on("text-change", () => {
      const html = quill.root.innerHTML;
      onChangeRef.current(html === "<p><br></p>" ? "" : html);
    });

    return () => {
      quillRef.current = null;
      if (container) container.innerHTML = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync external value changes into the editor without disrupting active typing.
  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;
    const incoming = value || "";
    const current = quill.root.innerHTML === "<p><br></p>" ? "" : quill.root.innerHTML;
    if (incoming !== current && !quill.hasFocus()) {
      quill.clipboard.dangerouslyPasteHTML(incoming);
    }
  }, [value]);

  return (
    <div>
      {label && <label className="block text-xs font-semibold uppercase tracking-wider text-[var(--mf-grey)] mb-1.5">{label}</label>}
      <div className="mf-quill bg-white" ref={containerRef} data-testid="rich-text-editor" />
    </div>
  );
}
